package com.ilussobsa.liveauction

import com.ilussobsa.AnimationAction
import com.ilussobsa.Bid
import com.ilussobsa.HasTimestamp
import com.ilussobsa.LiveAuctionData
import com.ilussobsa.sdk.currentUser
import com.ilussobsa.utils.AuctioneerPack
import com.ilussobsa.utils.EventResources
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.now
import com.lightningkite.reactive.context.*
import com.lightningkite.reactive.core.*
import com.lightningkite.reactive.extensions.*
import com.lightningkite.reactive.lensing.*
import com.lightningkite.readable.*
import kotlin.time.Duration.Companion.seconds
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.datetime.Instant

class AuctionController(
    val liveAuctionData: Reactive<LiveAuctionData?>,
    val currentLotBids: Reactive<List<Bid>>,
    val auctioneerPack: Reactive<AuctioneerPack?>,
    val presenter: AuctionPresenter,
    coroutineScope: CoroutineScope,
) {

    val currentLotId = remember { liveAuctionData()?.currentVehicle }
    val isLive = remember {
        liveAuctionData()?.laneStartedAt?.let { it <= nowBySecond() } ?: false
    }
    val biddingOpen = remember {
        liveAuctionData()?.completion == null && isLive()
    }
    val countdown = remember {
        liveAuctionData()?.laneStartedAt?.let { it - nowBySecond() }
    }
    val auctionRoomOpen = remember {
        val opensAt = liveAuctionData()?.laneStartedAt?.let { it - 60.seconds }
        opensAt?.let { it <= nowBySecond() } ?: false
    }
    val nowBySecond = reactiveProcess {
        while (true) {
            emit(now())
            delay(1.seconds)
        }
    }

    private var permitPoke = true
    private var goingOncePlayed = false
    private var goingTwicePlayed = false
    private var lastAsk = 0
    private var soldPlayed = false
    private var reserveMetPlayed = false
    private var lastBids = 0

    init {
        coroutineScope.apply {
            // Handle next-up on the block
            reactiveSuspending {
                rerunOn(currentLotId)
                if (!isLive.awaitOnce()) return@reactiveSuspending
                println("Handling next-up on the block")
                goingOncePlayed = false
                goingTwicePlayed = false
                soldPlayed = false
                lastAsk = 0
//                val l = liveAuctionData.awaitOnce()
//                if (l != null && l.completion == null) {
//                    playAudio(AuctioneerPack::next)
//                }
            }

            // Handle drop to bottom asking price
            reactiveSuspending {
                val l = liveAuctionData() ?: return@reactiveSuspending
                val newAsk = l.asking
                if (newAsk < lastAsk) {
                    if (l.bids == 0 && l.dropsLeft == 0)
                        playAudio(AuctioneerPack::bottomPrice)
                }
                lastAsk = newAsk
            }

            // Handle what happens when bids are placed (includes live money interjection)
            reactiveSuspending {
                val l = liveAuctionData() ?: return@reactiveSuspending
                val newCount = l.bids
                println("newCount $newCount")
                if (newCount == 0) {
                    reserveMetPlayed = false
                    lastBids = 0
                }
                println("lastBids $lastBids")
                println("reserveMetPlayed $reserveMetPlayed")
                println("l.reserveMet ${l.reserveMet}")
                if (newCount > lastBids) {
                    println("newCount > lastBids")
                    playAudio(AuctioneerPack::bidPlaced)
                    if (!reserveMetPlayed && l.reserveMet && newCount > 0) {
                        reserveMetPlayed = true
                        playAudio(AuctioneerPack::reserveMet)
                        if (newCount == 1) {
                            presenter.playAnimation(AnimationAction.LIVE_MONEY)
                        }
                        permitPoke = true
                        delay(100)
                    } else if (newCount == 1) {
                        presenter.playAnimation(AnimationAction.LIVE_MONEY)
                        playAudio(AuctioneerPack::firstBidPlaced)
                    } else {
                        println("Bidding action")
                        presenter.playAnimation(AnimationAction.BIDDING_ACTION)
                    }
                }
                lastBids = newCount
            }

            // Handle what happens when the auction is about to time out (includes going once, going twice, etc.)
            reactiveSuspending {
                val goingStatus = liveAuctionData()?.finalLaneTimeout?.let {
                    val countdown = it - nowBySecond() - LiveAuctionData.lagAccounting
                    when {
                        countdown < LiveAuctionData.goingDuration -> 2
                        countdown < LiveAuctionData.goingDuration * 2 -> 1
                        else -> null
                    }
                }
                when (val gs = goingStatus.also { println("goingStatus: $it") }) {
                    2 -> {
                        if (!goingTwicePlayed) {
                            goingTwicePlayed = true
                            playAudio(AuctioneerPack::goingTwice)
                            presenter.playAnimation(AnimationAction.COUNTING_1_OUT_ACTION)
                            delay(100)
                            presenter.playAnimation(AnimationAction.COUNTING_2_ACTION)
                            delay(100)
                        }
                    }

                    1 -> {
                        if (!goingOncePlayed) {
                            goingOncePlayed = true
                            playAudio(AuctioneerPack::goingOnce)
                            presenter.playAnimation(AnimationAction.COUNTING_1_ACTION)
                            delay(50)
                        }
                    }

                    null -> {
                        goingOncePlayed = false
                        goingTwicePlayed = false
                    }
                }
            }

            // Handle what happens when the current block ends (sold or no sale)
            reactiveSuspending {
                liveAuctionData()?.completion?.let {
                    val noSale = liveAuctionData()?.reserveMet != true
                    println("reserve met? ${liveAuctionData()?.reserveMet}")
                    if (!soldPlayed) {
                        soldPlayed = true
                        if (noSale) {
                            println("No sale!")
                            playAudio(AuctioneerPack::noSale, true)
                        } else {
                            println("Sold!")
                            playAudio(AuctioneerPack::sold, true)
                        }
                        presenter.playAnimation(if (noSale) AnimationAction.NO_SALE_ACTION else AnimationAction.SOLD_ACTION)
                    }
                }
            }

            // Issue pokes
            reactiveSuspending {
                val shouldPoke = currentLotBids().let {
                    it.getOrNull(it.lastIndex - 1)?.buyer == currentUser()?._id &&
                            it.getOrNull(it.lastIndex)?.buyer != currentUser()?._id &&
                            liveAuctionData()?.reserveMet == true
                }

                if (shouldPoke) {
                    delay(2000)
                    if (permitPoke) {
                        presenter.performPoke(LocalPoke(
                            auctioneerPack()?.pokes?.random()?.text ?: "Don't let it go!"
                        ))
                    }
                    permitPoke = false
                }
            }


        }
    }

    private suspend fun playAudio(getter: (AuctioneerPack) -> EventResources, withGavel: Boolean = false) {
        auctioneerPack()?.let(getter)?.let {
            presenter.playAudio(it, withGavel)
        }
    }
}

interface AuctionPresenter {
    fun playAnimation(action: AnimationAction)
    suspend fun playAudio(event: EventResources, withGavel: Boolean = false)
    fun performPoke(poke: LocalPoke)
}

data class LocalPoke(
    val message: String,
    override val at: Instant = now(),
) : HasTimestamp